import {
    Alert,
    Autocomplete,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { AxiosWithAuth } from '../../Utilities/authenticationService'
import { AUTOSCHEDULE_QUALITY_TAGS, CONFIRMED_SHIFT_CHANGE_REASONS } from '../../constants'

const CHANGE_LABELS = {
    added: '追加',
    removed: '削除',
    time_shifted: '時間変更'
}

const TAG_LABELS = Object.fromEntries(AUTOSCHEDULE_QUALITY_TAGS.map(tag => [tag.value, tag.label]))
const REASON_LABELS = Object.fromEntries(CONFIRMED_SHIFT_CHANGE_REASONS.map(reason => [reason.value, reason.label]))

const formatReasons = reasons => {
    if (!reasons?.length) return '—'
    return reasons.map(reason => REASON_LABELS[reason] || reason).join('、')
}

const formatRange = interval => {
    if (!interval?.start_label || !interval?.end_label) return '—'
    return `${interval.start_label}–${interval.end_label}`
}

const flattenEmployeeRows = employees =>
    (employees || []).flatMap(employee => {
        const rows = []
        const reasons = employee.change_reasons || []
        ;(employee.removed || []).forEach(interval => {
            rows.push({
                key: `${employee.employment_id}-removed-${interval.start_time}`,
                name: employee.name,
                kind: 'removed',
                proposed: interval,
                current: null,
                reasons
            })
        })
        ;(employee.added || []).forEach(interval => {
            rows.push({
                key: `${employee.employment_id}-added-${interval.start_time}`,
                name: employee.name,
                kind: 'added',
                proposed: null,
                current: interval,
                reasons
            })
        })
        ;(employee.time_shifted || []).forEach(change => {
            rows.push({
                key: `${employee.employment_id}-shifted-${change.from?.start_time}-${change.to?.start_time}`,
                name: employee.name,
                kind: 'time_shifted',
                proposed: change.from,
                current: change.to,
                reasons
            })
        })
        if (!rows.length && reasons.length) {
            rows.push({
                key: `${employee.employment_id}-reason`,
                name: employee.name,
                kind: null,
                proposed: null,
                current: null,
                reasons
            })
        }
        return rows
    })

const flattenFusokuRows = fusoku => {
    if (!fusoku) return []
    const rows = []
    ;(fusoku.removed || []).forEach(interval => {
        rows.push({
            key: `fusoku-removed-${interval.start_time}-${interval.row_number}`,
            name: '不足',
            kind: 'removed',
            proposed: interval,
            current: null,
            reasons: []
        })
    })
    ;(fusoku.added || []).forEach(interval => {
        rows.push({
            key: `fusoku-added-${interval.start_time}-${interval.row_number}`,
            name: '不足',
            kind: 'added',
            proposed: null,
            current: interval,
            reasons: []
        })
    })
    ;(fusoku.time_shifted || []).forEach(change => {
        rows.push({
            key: `fusoku-shifted-${change.from?.start_time}-${change.to?.start_time}`,
            name: '不足',
            kind: 'time_shifted',
            proposed: change.from,
            current: change.to,
            reasons: []
        })
    })
    return rows
}

const AdminAutoscheduleSnapshots = () => {
    const [stores, setStores] = useState([])
    const [selectedStore, setSelectedStore] = useState(null)
    const [opsDate, setOpsDate] = useState('')
    const [snapshots, setSnapshots] = useState([])
    const [selectedSnapshotId, setSelectedSnapshotId] = useState('')
    const [detail, setDetail] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        AxiosWithAuth.get('/stores')
            .then(res => setStores(res.data || []))
            .catch(() => setError('店舗一覧の取得に失敗しました'))
    }, [])

    useEffect(() => {
        if (!selectedStore || !opsDate) {
            setSnapshots([])
            setSelectedSnapshotId('')
            setDetail(null)
            return
        }

        let cancelled = false
        setLoading(true)
        AxiosWithAuth.get('/autoschedule_snapshots', {
            params: { store_id: selectedStore.id, ops_date: opsDate }
        })
            .then(res => {
                if (cancelled) return
                const list = res.data || []
                setSnapshots(list)
                setSelectedSnapshotId(list[0] ? String(list[0].id) : '')
                if (!list[0]) setDetail(null)
            })
            .catch(() => {
                if (cancelled) return
                setError('自動作成シフトの取得に失敗しました')
                setSnapshots([])
                setSelectedSnapshotId('')
                setDetail(null)
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [selectedStore, opsDate])

    useEffect(() => {
        if (!selectedSnapshotId) return

        let cancelled = false
        setLoading(true)
        AxiosWithAuth.get(`/autoschedule_snapshots/${selectedSnapshotId}`)
            .then(res => {
                if (cancelled) return
                setDetail(res.data)
            })
            .catch(() => {
                if (cancelled) return
                setError('比較の取得に失敗しました')
                setDetail(null)
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [selectedSnapshotId])

    const rows = [...flattenEmployeeRows(detail?.diff?.employees), ...flattenFusokuRows(detail?.diff?.fusoku)]

    return (
        <Box sx={{ margin: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: '960px' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    AI提案と確定シフト
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Autocomplete
                        sx={{ minWidth: 280, flex: 1 }}
                        options={stores}
                        value={selectedStore}
                        getOptionLabel={option => `${option.seg_code} - ${option.name}`}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, newValue) => setSelectedStore(newValue)}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="店舗"
                                placeholder="選択してください"
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                    />
                    <TextField
                        type="date"
                        label="日付"
                        value={opsDate}
                        onChange={event => setOpsDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'data-testid': 'ops-date' }}
                    />
                    {snapshots.length > 1 && (
                        <FormControl sx={{ minWidth: 220 }}>
                            <InputLabel id="snapshot-created-at-label">自動作成日時</InputLabel>
                            <Select
                                labelId="snapshot-created-at-label"
                                label="自動作成日時"
                                value={selectedSnapshotId}
                                onChange={event => setSelectedSnapshotId(event.target.value)}
                            >
                                {snapshots.map(snapshot => (
                                    <MenuItem key={snapshot.id} value={String(snapshot.id)}>
                                        {new Date(snapshot.created_at).toLocaleString('ja-JP')}
                                        {snapshot.tag ? `（${TAG_LABELS[snapshot.tag] || snapshot.tag}）` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && selectedStore && opsDate && snapshots.length === 0 && (
                    <Typography>この日の自動作成シフトはありません</Typography>
                )}

                {!loading && detail && (
                    <Typography sx={{ mb: 1 }}>
                        評価: {TAG_LABELS[detail.tag] || '未評価'}
                    </Typography>
                )}
                {!loading && detail && rows.length === 0 && snapshots.length > 0 && (
                    <Typography>自動作成時と現在のシフトは同じです</Typography>
                )}

                {!loading && rows.length > 0 && (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>従業員</TableCell>
                                <TableCell>種別</TableCell>
                                <TableCell>自動作成</TableCell>
                                <TableCell>現在</TableCell>
                                <TableCell>変更理由</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.key}>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{CHANGE_LABELS[row.kind] || '—'}</TableCell>
                                    <TableCell>{formatRange(row.proposed)}</TableCell>
                                    <TableCell>{formatRange(row.current)}</TableCell>
                                    <TableCell>{formatReasons(row.reasons)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
            <Snackbar open={Boolean(error)} autoHideDuration={4000} onClose={() => setError('')}>
                <Alert severity="error" onClose={() => setError('')}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default AdminAutoscheduleSnapshots
